import { useEffect, useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Button, Modal } from "@impero-com/ui";
import { notification } from "antd";

import {
    TestingActivityCurrentStep,
    TestingActivityAssignmentKind,
    TestingActivityReassignment,
} from "api/generated/model";
import { UserTypeaheadInput } from "components/form/fields/userTypeaheadInput";

import { useReassignTestingActivitiesMutation } from "./queries/useReassignTestingActivityMutation";

interface ReassignTextingActivitiesModalProps {
    open: boolean;
    close: () => void;
    deselectAll: () => void;
    currentStep: TestingActivityCurrentStep;
    testingActivityIds: string[];
    hasReviewStep: boolean;
    hasSecondReviewStep: boolean;
}

function currentStepToReassignmentStep(
    currentStep: TestingActivityCurrentStep
): TestingActivityAssignmentKind {
    switch (currentStep) {
        case "completed":
            throw new Error("Can not reassign Completed step");
        case "responsible":
        case "firstReviewer":
        case "secondReviewer":
            return currentStep;
        default:
            return assertNever(currentStep);
    }
}

const createMutationPayload = (
    testingActivityIds: string[],
    responsibleId: number | null,
    firstReviewerId: number | null,
    secondReviewerId: number | null
) => {
    return testingActivityIds.flatMap((testingActivityId) => {
        const reassignments: TestingActivityReassignment[] = [];

        if (responsibleId) {
            reassignments.push({
                activityId: testingActivityId,
                newUser: responsibleId,
                step: currentStepToReassignmentStep("responsible"),
            });
        }

        if (firstReviewerId) {
            reassignments.push({
                activityId: testingActivityId,
                newUser: firstReviewerId,
                step: currentStepToReassignmentStep("firstReviewer"),
            });
        }

        if (secondReviewerId) {
            reassignments.push({
                activityId: testingActivityId,
                newUser: secondReviewerId,
                step: currentStepToReassignmentStep("secondReviewer"),
            });
        }
        return reassignments;
    });
};

export function ReassignTestingActivitiesModal({
    open,
    close,
    deselectAll,
    currentStep,
    testingActivityIds,
    hasReviewStep,
    hasSecondReviewStep,
}: ReassignTextingActivitiesModalProps) {
    const { t } = useTranslation();
    const [responsibleId, setResponsibleId] = useState<number | null>(null);
    const [firstReviewerId, setFirstReviewerId] = useState<number | null>(null);
    const [secondReviewerId, setSecondReviewerId] = useState<number | null>(
        null
    );
    const { mutateAsync, isPending } = useReassignTestingActivitiesMutation();

    // Reset form values when modal opens
    useEffect(() => {
        if (open) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setResponsibleId(null);
            setFirstReviewerId(null);
            setSecondReviewerId(null);
        }
    }, [open]);

    const okButton = {
        label: t("Reassign"),
        loading: isPending,
        disabled:
            (responsibleId === null &&
                firstReviewerId === null &&
                secondReviewerId === null) ||
            currentStep === null,
        onClick: () => {
            if (
                responsibleId === null &&
                firstReviewerId === null &&
                secondReviewerId === null
            ) {
                throw new Error(
                    "Assigned users can not be null, OK button should be disabled"
                );
            }
            if (currentStep === null) {
                throw new Error(
                    "Current step can not be null, modal should not be displayed"
                );
            }

            const payload = createMutationPayload(
                testingActivityIds,
                responsibleId,
                firstReviewerId,
                secondReviewerId
            );

            mutateAsync({ payload }).then(() => {
                notification.success({
                    message: t("TestingActivities_ReassignModal_Success"),
                });
                deselectAll();
                close();
            });
        },
    };

    const cancelButton = {
        label: t("Cancel"),
        onClick: close,
    };

    const canChangeResponsible = currentStep === "responsible";
    const canChangeFirstReviewer =
        (currentStep === "responsible" || currentStep === "firstReviewer") &&
        hasReviewStep;
    const canChangeSecondReviewer = hasSecondReviewStep;

    return (
        <Modal
            variant="content"
            open={open}
            onClose={cancelButton.onClick}
            title={t("TestingActivities_ReassignModal_Title")}
            footer={
                <>
                    <Button onClick={cancelButton.onClick}>
                        {cancelButton.label}
                    </Button>
                    <Button
                        variant="primary"
                        loading={okButton.loading}
                        disabled={okButton.disabled}
                        onClick={okButton.onClick}
                    >
                        {okButton.label}
                    </Button>
                </>
            }
        >
            {canChangeResponsible && (
                <UserTypeaheadInput
                    label={t("TestingActivities_ReassignModal_NewResponsible")}
                    value={responsibleId}
                    onChange={setResponsibleId}
                />
            )}
            {canChangeFirstReviewer && (
                <UserTypeaheadInput
                    label={t("TestingActivities_ReassignModal_NewReviewer")}
                    value={firstReviewerId}
                    onChange={setFirstReviewerId}
                />
            )}
            {canChangeSecondReviewer && (
                <UserTypeaheadInput
                    label={t(
                        "TestingActivities_ReassignModal_NewSecondReviewer"
                    )}
                    value={secondReviewerId}
                    onChange={setSecondReviewerId}
                />
            )}
        </Modal>
    );
}
